import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import { gql, useMutation } from "@apollo/client";
import {
  AutopilotSessionContinueSearchModal_RestartAutopilotSessionMutation,
  AutopilotSessionContinueSearchModal_RestartAutopilotSessionMutationVariables,
  AutopilotSessionRunningState,
} from "../../../__generated__/graphql";
import toast from "react-hot-toast";
import CiroModal from "../../shared/CiroModal";
import { useContext, useState } from "react";
import CiroTextInput from "../../shared/CiroTextInput";
import { useAutopilotContactCountValidation } from "../../../hooks/useAutopilotContactCountValidation";
import NavigationContext from "../../../contexts/NavigationContext";
import MonthlyContactsRemaining from "../../shared/MonthlyContactsRemaining";
import CiroLink from "../../shared/CiroLink";
import { PRICING_PAGE_LINK } from "../../../utils/consts";
import AutopilotContext from "../../../contexts/AutopilotContext";
import Loading from "../../shared/Loading";
import AutopilotRunSkipContactsContainer from "../shared/AutopilotRunSkipContactsContainer";

const AutopilotSessionContinueSearchModal_RestartAutopilotSession = gql`
  mutation AutopilotSessionContinueSearchModal_RestartAutopilotSession(
    $id: Int!
    $additionalContacts: Int!
    $skipContactsOptions: SkipContactsOptionsInput
  ) {
    restartAutopilotInternalSession(
      id: $id
      additionalContacts: $additionalContacts
      skipContactsOptions: $skipContactsOptions
    ) {
      runningState {
        status
        currentContacts {
          id
          firstName
          lastName
          companyName
          title
        }
        contactsSaved
        totalContactsToSave
        multisearchDetail {
          currentCompanyName
          countCompaniesSearched
          totalCompaniesToSearch
        }
      }
      success
      message
    }
  }
`;

const AutopilotSessionContinueSearchModal = ({
  isOpen,
  onClose,
  autopilotSessionId,
  initialSkipContactsYouveAlreadySeen,
  onRestartedSession,
  contactsScanned,
  totalProfiles,
  runningState,
  customName,
}: {
  isOpen: boolean;
  onClose: () => void;
  autopilotSessionId: number;
  initialSkipContactsYouveAlreadySeen: boolean;
  onRestartedSession: (
    runningState: AutopilotSessionRunningState | null,
  ) => void;
  contactsScanned?: number | null;
  totalProfiles?: number | null;
  runningState: AutopilotSessionRunningState | null;
  customName?: string | null;
}) => {
  const [additionalContacts, setAdditionalContacts] = useState(50); // Default value
  const [upgradeMessage, setUpgradeMessage] = useState<string | null>(null);

  const {
    maxNumberContactsPerAutopilotSearch,
    maxNumberContactsPerAutopilotSearchLoading,
  } = useContext(AutopilotContext);

  const { autopilotContactLimitDetails } = useContext(NavigationContext);
  const { validateContactCount } = useAutopilotContactCountValidation({
    totalProfiles,
    contactsScanned,
    runningState,
  });

  const [skipContactsYouveAlreadySeen, setSkipContactsYouveAlreadySeen] =
    useState(initialSkipContactsYouveAlreadySeen);

  const validationResult = validateContactCount(additionalContacts);

  const [
    restartAutopilotInternalSession,
    { loading: restartAutopilotInternalSessionLoading },
  ] = useMutation<
    AutopilotSessionContinueSearchModal_RestartAutopilotSessionMutation,
    AutopilotSessionContinueSearchModal_RestartAutopilotSessionMutationVariables
  >(AutopilotSessionContinueSearchModal_RestartAutopilotSession);

  return (
    <CiroModal isOpen={isOpen} onClose={onClose}>
      <div>
        {maxNumberContactsPerAutopilotSearchLoading ? (
          <div className={classNames("text-center", "py-8")}>
            <div className={classNames("text-lg", "font-semibold", "mb-2")}>
              Loading...
            </div>
            <Loading size="SMALL" />
          </div>
        ) : (
          <>
            <div className={classNames("text-lg", "font-semibold", "mb-2")}>
              {customName ?? "Keep searching"}
            </div>
            <div className={classNames("mb-4")}>
              How many additional contacts would you like to analyze?
            </div>
            <MonthlyContactsRemaining
              autopilotContactLimitDetails={autopilotContactLimitDetails}
              customClassName="mb-4"
            />
            <div className={classNames("mb-4")}>
              <CiroTextInput
                type="number"
                min="1"
                max={maxNumberContactsPerAutopilotSearch?.toString() ?? "100"}
                value={additionalContacts.toString()}
                onChange={(e) => {
                  const newValue = Math.max(1, parseInt(e.target.value) || 1);
                  setAdditionalContacts(newValue);
                  const validation = validateContactCount(newValue);
                  setUpgradeMessage(validation.upgradeMessage);
                }}
                placeholder={`Enter number of contacts (max ${maxNumberContactsPerAutopilotSearch?.toLocaleString() ?? "100"} per search)`}
                containerClassName="border-0"
              />
              {validationResult.error && (
                <div className={classNames("text-red-500", "text-xs", "mt-2")}>
                  {validationResult.error}
                </div>
              )}
              {upgradeMessage && (
                <div className={classNames("mt-2", "text-gray-600", "text-sm")}>
                  <CiroLink unstyled href={PRICING_PAGE_LINK}>
                    <span>{upgradeMessage}</span>
                    <span className={classNames("ml-1", "text-orange-400")}>
                      →
                    </span>
                  </CiroLink>
                </div>
              )}
            </div>
            <div className={classNames("mb-4")}>
              <AutopilotRunSkipContactsContainer
                skipContactsYouveAlreadySeen={skipContactsYouveAlreadySeen}
                setSkipContactsYouveAlreadySeen={
                  setSkipContactsYouveAlreadySeen
                }
              />
            </div>
            <div className={classNames("flex", "gap-2", "justify-end")}>
              <CiroButton
                analyticsField="Cancel restart autopilot session"
                onClick={onClose}
                disabled={restartAutopilotInternalSessionLoading}
              >
                Cancel
              </CiroButton>
              <CiroButton
                analyticsField="Confirm restart autopilot session"
                disabled={validationResult.error != null}
                onClick={() => {
                  restartAutopilotInternalSession({
                    variables: {
                      id: autopilotSessionId,
                      additionalContacts: additionalContacts,
                      skipContactsOptions: {
                        skipContactsYouveAlreadySeen,
                      },
                    },
                    onCompleted: (data) => {
                      onRestartedSession(
                        data.restartAutopilotInternalSession.runningState ??
                          null,
                      );
                      onClose();
                      toast.success(
                        `Restarted search to analyze ${additionalContacts} more contacts`,
                      );
                    },
                    onError: (error) => {
                      console.error(error);
                      toast.error("Error restarting autopilot session");
                    },
                  });
                }}
                style={CiroButtonStyleEnum.LOUD}
              >
                Continue search
              </CiroButton>
            </div>
          </>
        )}
      </div>
    </CiroModal>
  );
};

export default AutopilotSessionContinueSearchModal;
